import * as pdfjsLib from "pdfjs-dist";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { useEffect, useRef, useState } from "react";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

// Serve pdf.js's standard fonts and cMaps from jsDelivr pinned to the bundled
// version. pdf.js uses these as @font-face sources so glyph metrics match the
// PDF's embedded fonts. Without them, long tokens like emails wrap mid-glyph
// (e.g. "long.example.address@gmail.c" on line 1, "om" on line 2) because the
// fallback font computes different advances than the embedded font.
const PDFJS_ASSET_BASE = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}`;
const STANDARD_FONT_URL = `${PDFJS_ASSET_BASE}/standard_fonts/`;
const CMAP_URL = `${PDFJS_ASSET_BASE}/cmaps/`;

export interface PreviewIdentity {
  username: string;
  fullName: string;
  headline: string;
  location: string;
  email: string;
  followerCount: number;
  connectionsCount: number;
  currentRole: string;
  currentCompany: string;
  positionsCount: number;
  educationsCount: number;
}

export interface PreviewViewProps {
  identity: PreviewIdentity;
  pdfFile?: File | null;
  onConfirm: () => void;
  onBack: () => void;
  confirming: boolean;
}

function looksLikeJunk(value: string): boolean {
  return /@|https?:\/\/|www\./i.test(value);
}

function cleanName(value: string): string {
  return looksLikeJunk(value) ? "" : value.trim();
}

function formatCount(n: number): string {
  if (!n) return "0";
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return n.toLocaleString("pt-BR");
}

interface PdfPreviewProps {
  file: File;
}

function PdfPreview({ file }: PdfPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [pdf, setPdf] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [pageNum, setPageNum] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load the document once per file.
  useEffect(() => {
    let cancelled = false;
    let loadedDoc: pdfjsLib.PDFDocumentProxy | null = null;
    setLoading(true);
    setError(null);
    setPdf(null);
    setPageNum(1);

    (async () => {
      try {
        const buffer = await file.arrayBuffer();
        if (cancelled) return;
        const task = pdfjsLib.getDocument({
          data: new Uint8Array(buffer),
          // Security hardening: no eval, no external fetches of the doc body.
          isEvalSupported: false,
          disableAutoFetch: true,
          disableStream: true,
          // Let pdf.js install the PDF's embedded fonts via @font-face. When
          // disableFontFace is true, pdf.js renders glyphs with canvas fillText
          // using a browser fallback — the fallback's widths don't match the
          // embedded font, so advances drift and long tokens ("…@gmail.c" /
          // "om") wrap mid-word. With @font-face enabled plus the standard
          // font pack, every glyph renders from the font the PDF was authored
          // against.
          disableFontFace: false,
          useSystemFonts: false,
          standardFontDataUrl: STANDARD_FONT_URL,
          cMapUrl: CMAP_URL,
          cMapPacked: true,
        });
        loadedDoc = await task.promise;
        if (cancelled) {
          await loadedDoc.destroy();
          return;
        }
        setPdf(loadedDoc);
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message || "Não foi possível ler o PDF");
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      loadedDoc?.destroy().catch(() => {});
    };
  }, [file]);

  // Render the current page when pdf or pageNum changes.
  useEffect(() => {
    if (!pdf) return;
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    let cancelled = false;
    let renderTask: pdfjsLib.RenderTask | null = null;
    setLoading(true);

    (async () => {
      try {
        const page = await pdf.getPage(pageNum);
        if (cancelled) return;

        const baseViewport = page.getViewport({ scale: 1 });
        const containerWidth = container.clientWidth || 640;
        const scale = Math.min(2.5, Math.max(0.5, containerWidth / baseViewport.width));
        const viewport = page.getViewport({ scale });
        const dpr = Math.min(2, window.devicePixelRatio || 1);

        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;

        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Canvas 2D indisponível");
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        renderTask = page.render({ canvasContext: ctx, viewport, canvas });
        await renderTask.promise;
        if (!cancelled) setLoading(false);
      } catch (err) {
        // Render cancellations throw — ignore if we aborted intentionally.
        if (cancelled) return;
        const name = (err as { name?: string }).name;
        if (name === "RenderingCancelledException") return;
        setError((err as Error).message || "Falha ao renderizar a página");
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [pdf, pageNum]);

  const total = pdf?.numPages ?? 0;
  const canPrev = pageNum > 1;
  const canNext = pageNum < total;

  if (error) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span className="pill alert no-dot">Não foi possível renderizar o PDF</span>
        <div style={{ fontSize: 12, color: "var(--r-ink-muted)", lineHeight: 1.55 }}>
          {error}. A avaliação ainda pode seguir — conferimos o texto extraído acima.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div
        ref={containerRef}
        style={{
          position: "relative",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          maxHeight: 520,
          overflow: "auto",
          padding: 12,
          borderRadius: 10,
          border: "1px solid var(--r-card-line)",
          background: "var(--r-sand-step)",
        }}
        data-testid="labs-ci-pdf-preview"
      >
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              background: "rgba(255,255,255,0.7)",
              backdropFilter: "blur(4px)",
              padding: 24,
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: 280,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <div className="skel" />
              <div className="skel" style={{ width: "80%" }} />
              <div className="skel" style={{ width: "60%" }} />
            </div>
            <span style={{ fontSize: 12, color: "var(--r-ink-muted)" }}>
              Renderizando
              <span className="ell">
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </span>
            </span>
          </div>
        )}
        <canvas
          ref={canvasRef}
          style={{ display: "block", border: "1px solid var(--r-card-line)" }}
          aria-label={`Página ${pageNum} do PDF enviado`}
        />
      </div>
      {total > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: 12,
          }}
        >
          <button
            type="button"
            disabled={!canPrev || loading}
            onClick={() => setPageNum((n) => Math.max(1, n - 1))}
            data-testid="labs-ci-pdf-prev"
            className="btn"
            style={{ height: 30, padding: "0 12px", fontSize: 13 }}
          >
            Anterior
          </button>
          <span style={{ color: "var(--r-ink-muted)", fontVariantNumeric: "tabular-nums" }}>
            Página {pageNum} de {total}
          </span>
          <button
            type="button"
            disabled={!canNext || loading}
            onClick={() => setPageNum((n) => Math.min(total, n + 1))}
            data-testid="labs-ci-pdf-next"
            className="btn"
            style={{ height: 30, padding: "0 12px", fontSize: 13 }}
          >
            Próxima
          </button>
        </div>
      )}
    </div>
  );
}

export function PreviewView({
  identity,
  pdfFile,
  onConfirm,
  onBack,
  confirming,
}: PreviewViewProps) {
  const displayName = cleanName(identity.fullName);
  const displayHeadline =
    identity.headline && !looksLikeJunk(identity.headline) ? identity.headline : "";
  const currentPosition = [identity.currentRole, identity.currentCompany]
    .filter(Boolean)
    .join(" · ");

  const showPdf = !!pdfFile;
  const [pdfOpen, setPdfOpen] = useState(false);
  const sourcePill = pdfFile ? "PDF" : "LinkedIn";

  return (
    <div className="ci-stripe ci-stripe-page">
      <div className="ci-mid fade-up" style={{ width: "100%" }}>
        <div style={{ marginBottom: 20 }}>
          <p className="ci-eyebrow">Confirmar perfil</p>
          <h1 className="ci-title">É esse o candidato que você quer avaliar?</h1>
          <p className="ci-sub">
            A avaliação completa demanda alguns minutos. Confirme antes de prosseguir.
          </p>
        </div>

        <div className="card">
          <div className="card-head">
            <div className="card-head-left">
              <h2>Confira o perfil</h2>
              <div className="sub">
                Os dados abaixo foram lidos da fonte e serão usados na avaliação.
              </div>
            </div>
            <div className="card-head-right">
              <span className="pill muted no-dot">Fonte · {sourcePill}</span>
            </div>
          </div>

          <div className="card-body padded">
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  lineHeight: 1.2,
                  color: "var(--r-ink)",
                  wordBreak: "break-word",
                }}
              >
                {displayName || "(sem nome)"}
              </div>
              {displayHeadline && (
                <div
                  style={{
                    fontSize: 14,
                    color: "var(--r-ink-muted)",
                    lineHeight: 1.55,
                    wordBreak: "break-word",
                  }}
                >
                  {displayHeadline}
                </div>
              )}
            </div>

            <div style={{ height: 1, background: "var(--r-card-line)", margin: "16px 0" }} />

            <div className="kv">
              {identity.email && (
                <div className="kv-item">
                  <div className="key">Email</div>
                  <div className="val" title={identity.email} style={{ wordBreak: "break-all" }}>
                    {identity.email}
                  </div>
                </div>
              )}
              {currentPosition && (
                <div className="kv-item">
                  <div className="key">Cargo atual</div>
                  <div className="val">{currentPosition}</div>
                </div>
              )}
              {identity.location && (
                <div className="kv-item">
                  <div className="key">Local</div>
                  <div className="val">{identity.location}</div>
                </div>
              )}
              {identity.educationsCount > 0 && (
                <div className="kv-item">
                  <div className="key">Formação</div>
                  <div className="val">{identity.educationsCount}</div>
                </div>
              )}
              {identity.positionsCount > 0 && (
                <div className="kv-item">
                  <div className="key">Posições</div>
                  <div className="val">{identity.positionsCount}</div>
                </div>
              )}
              {identity.connectionsCount > 0 && (
                <div className="kv-item">
                  <div className="key">Conexões</div>
                  <div className="val">{formatCount(identity.connectionsCount)}</div>
                </div>
              )}
              {identity.followerCount > 0 && (
                <div className="kv-item">
                  <div className="key">Seguidores</div>
                  <div className="val">{formatCount(identity.followerCount)}</div>
                </div>
              )}
            </div>
          </div>

          {showPdf && pdfFile && (
            <>
              <div style={{ height: 1, background: "var(--r-card-line)" }} />
              <button
                type="button"
                onClick={() => setPdfOpen((v) => !v)}
                aria-expanded={pdfOpen}
                data-testid="labs-ci-pdf-toggle"
                style={{
                  appearance: "none",
                  background: "transparent",
                  border: "none",
                  width: "100%",
                  padding: "14px 24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  textAlign: "left",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                <span style={{ display: "flex", alignItems: "baseline", gap: 10, minWidth: 0 }}>
                  <span style={{ fontWeight: 600, color: "var(--r-ink)", fontSize: 14 }}>
                    PDF original
                  </span>
                  <span style={{ fontSize: 12, color: "var(--r-ink-muted)" }}>
                    o LinkedIn quebra o email em duas linhas — já capturamos o valor correto acima
                  </span>
                </span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "var(--r-ink-muted)",
                    flexShrink: 0,
                  }}
                >
                  {pdfOpen ? "Ocultar" : "Mostrar"}
                </span>
              </button>
              {pdfOpen && (
                <div style={{ borderTop: "1px solid var(--r-card-line)", padding: 20 }}>
                  <PdfPreview file={pdfFile} />
                </div>
              )}
            </>
          )}

          <div className="card-foot">
            <button
              type="button"
              onClick={onBack}
              disabled={confirming}
              data-testid="labs-ci-preview-back"
              className="btn"
            >
              Voltar e trocar candidato
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={confirming}
              data-testid="labs-ci-preview-confirm"
              className="btn primary"
            >
              {confirming ? (
                <span>
                  Iniciando avaliação
                  <span className="ell">
                    <span>.</span>
                    <span>.</span>
                    <span>.</span>
                  </span>
                </span>
              ) : (
                "Avaliar candidato"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
